import { useEffect, useLayoutEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase';
import PublicHome from './pages/PublicHome';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminPage from './pages/AdminPage';
import VehicleDetailsPage from './pages/VehicleDetailsPage';
import SearchResultsPage from './pages/SearchResultsPage';
import AboutUsPage from './pages/AboutUsPage';
import ContactPage from './pages/ContactPage';
import SellYourCarPage from './pages/SellYourCarPage';
import FinancePage from './pages/FinancePage';
import WarrantyPage from './pages/WarrantyPage';
import PartExchangePage from './pages/PartExchangePage';
import ServicingPage from './pages/ServicingPage';
import EditVehiclePage from './pages/EditVehiclePage';
import ProtectedRoute from './components/ProtectedRoute';
import SiteFooter from './components/SiteFooter';
import { VehiclesProvider } from './context/VehiclesContext';
import './App.css';

function App() {
  const location = useLocation();
  const [authUser, setAuthUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    const scrollRoot = document.scrollingElement;
    if (scrollRoot) {
      scrollRoot.scrollTop = 0;
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      setAuthLoading(false);
    });

    return unsubscribe;
  }, []);

  if (authLoading) {
    return <div className="site-shell auth-loading">Checking authentication...</div>;
  }

  return (
    <div className="app-shell">
      <div className="app-main">
        <VehiclesProvider>
          <Routes>
            <Route path="/" element={<PublicHome authUser={authUser} />} />
            <Route path="/search" element={<SearchResultsPage authUser={authUser} />} />
            <Route path="/vehicle/:id" element={<VehicleDetailsPage authUser={authUser} />} />
            <Route path="/finance" element={<FinancePage authUser={authUser} />} />
            <Route
              path="/services/part-exchange"
              element={<PartExchangePage authUser={authUser} />}
            />
            <Route
              path="/services/looking-to-sell"
              element={<Navigate to="/sell-your-car" replace />}
            />
            <Route path="/sell-your-car" element={<SellYourCarPage authUser={authUser} />} />
            <Route
              path="/services/servicing"
              element={<ServicingPage authUser={authUser} />}
            />
            <Route path="/services/warranty" element={<WarrantyPage authUser={authUser} />} />
            <Route path="/about-us" element={<AboutUsPage authUser={authUser} />} />
            <Route path="/testimonials" element={<Navigate to="/contact" replace />} />
            <Route
              path="/contact"
              element={<ContactPage authUser={authUser} />}
            />
            <Route path="/showroom" element={<Navigate to="/search" replace />} />
            <Route path="/login" element={<LoginPage authUser={authUser} />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage authUser={authUser} />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute authUser={authUser}>
                  <AdminPage authUser={authUser} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/vehicles/:id/edit"
              element={
                <ProtectedRoute authUser={authUser}>
                  <EditVehiclePage authUser={authUser} />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </VehiclesProvider>
      </div>
      <SiteFooter />
    </div>
  );
}

export default App;
